import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ImagePopupComponent } from '../image-popup/image-popup';
import { Customer } from '../../../services/customer';
import { DeliveryService, PendingInvoiceListItem } from '../../../services/delivery.service';

@Component({
  selector: 'app-customer-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule
  ],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 class="dialog-title">Customer Details</h2>
        <button mat-icon-button class="close-button" (click)="onClose()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <div class="dialog-content">
        <div class="customer-name">
          <mat-icon class="name-icon">person</mat-icon>
          <span>{{ data.customer.name }}</span>
        </div>

        <div *ngIf="data.customer.pendingSync || data.customer.photoSyncPending" class="invoice-message">
          {{ data.customer.pendingSync ? 'Customer pending sync' : 'Shop photo pending sync' }}
        </div>

        <div class="phone-section" *ngIf="data.customer.phone">
          <mat-icon class="phone-icon">phone</mat-icon>
          <a [href]="'tel:' + data.customer.phone" class="phone-link" (click)="onPhoneClick($event)">
            {{ data.customer.phone }}
          </a>
        </div>

        <div class="balance-box" *ngIf="balanceAmount > 0">
          <span>Balance</span>
          <strong>{{ balanceAmount | currency: balanceCurrency : 'symbol' : '1.2-2' }}</strong>
        </div>

        <div class="invoice-section">
          <div class="invoice-title-row">
            <h3>Outstanding Invoices</h3>
            <div class="invoice-header-actions">
              <span *ngIf="invoicesLoaded && !loadingInvoices && pendingInvoices.length" class="invoice-count">
                {{ pendingInvoices.length }} invoice(s)
              </span>
              <button mat-icon-button
                      color="primary"
                      class="invoice-load-button"
                      [disabled]="loadingInvoices"
                      (click)="loadOutstandingInvoices()"
                      aria-label="Load outstanding invoices"
                      title="Load outstanding invoices">
                <mat-icon>{{ invoicesLoaded ? 'refresh' : 'download' }}</mat-icon>
              </button>
            </div>
          </div>

          <div *ngIf="loadingInvoices" class="invoice-message">Loading outstanding invoices...</div>
          <div *ngIf="invoiceError" class="invoice-error">{{ invoiceError }}</div>
          <div *ngIf="!loadingInvoices && !invoiceError && !invoicesLoaded" class="invoice-message">
            Tap the load button to view outstanding invoices.
          </div>
          <div *ngIf="!loadingInvoices && !invoiceError && invoicesLoaded && !pendingInvoices.length" class="invoice-message">
            No outstanding invoices.
          </div>

          <div *ngIf="pendingInvoices.length" class="invoice-list">
            <div class="invoice-row" *ngFor="let invoice of pendingInvoices">
              <div class="invoice-main">
                <span class="invoice-number">{{ invoice.invoiceNumber || ('INV-' + invoice.invoiceId) }}</span>
                <span class="invoice-date">{{ invoice.invoiceDate | date:'dd/MM/yyyy' }}</span>
              </div>
              <div class="invoice-amount">
                {{ invoice.amountResidual | currency: (invoice.currency || balanceCurrency) : 'symbol' : '1.2-2' }}
              </div>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <button *ngIf="hasLocation()" mat-raised-button color="primary" class="action-button" (click)="openMap()">
            <mat-icon>map</mat-icon>
            <span>Open Map</span>
          </button>

          <button *ngIf="hasLocation()" mat-raised-button class="action-button" (click)="openDirections()">
            <mat-icon>directions</mat-icon>
            <span>Directions</span>
          </button>

          <button *ngIf="data.customer.image" mat-raised-button color="accent" class="action-button" (click)="openImagePopup()">
            <mat-icon>image</mat-icon>
            <span>View Image</span>
          </button>

          <button *ngIf="data.showSelect" mat-raised-button color="accent" class="action-button select-button" (click)="loadLastInvoice()">
            <mat-icon>receipt_long</mat-icon>
            <span>Load Last Invoice</span>
          </button>

          <button *ngIf="data.showSelect" mat-raised-button color="primary" class="action-button select-button" (click)="selectCustomer()">
            <mat-icon>shopping_cart</mat-icon>
            <span>Select Customer</span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 0;
      min-width: 400px;
      max-width: 520px;
      max-height: 90vh;
      overflow: hidden;
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 22px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }

    .dialog-title {
      margin: 0;
      font-size: 20px;
      font-weight: 500;
    }

    .close-button {
      margin-left: auto;
    }

    .dialog-content {
      padding: 18px 22px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      max-height: calc(90vh - 66px);
      overflow-y: auto;
    }

    .customer-name,
    .phone-section {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .customer-name {
      font-size: 18px;
      font-weight: 600;
    }

    .name-icon {
      color: #1976d2;
    }

    .phone-section {
      font-size: 16px;
    }

    .phone-icon {
      color: #388e3c;
    }

    .phone-link {
      color: #1976d2;
      text-decoration: none;
      cursor: pointer;
      transition: color 0.2s;
    }

    .phone-link:hover {
      color: #1565c0;
      text-decoration: underline;
    }

    .balance-box {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #fff3f3;
      border: 1px solid #ffcdd2;
      border-radius: 8px;
      padding: 10px 12px;
      color: #d32f2f;
      font-size: 16px;
    }

    .invoice-section {
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 8px;
      padding: 10px;
      background: #fff;
    }

    .invoice-title-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 8px;
    }

    .invoice-title-row h3 {
      margin: 0;
      font-size: 17px;
      font-weight: 600;
    }

    .invoice-header-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
    }

    .invoice-load-button {
      width: 34px;
      height: 34px;
      line-height: 34px;
    }

    .invoice-load-button mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      line-height: 20px;
    }

    .invoice-count,
    .invoice-message {
      font-size: 13px;
      color: #666;
    }

    .invoice-error {
      font-size: 13px;
      color: #d32f2f;
    }

    .invoice-list {
      max-height: 210px;
      overflow-y: auto;
      border-top: 1px solid rgba(0, 0, 0, 0.08);
    }

    .invoice-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 8px 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    .invoice-row:last-child {
      border-bottom: 0;
    }

    .invoice-main {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .invoice-number {
      font-size: 13px;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .invoice-date {
      font-size: 12px;
      color: #666;
    }

    .invoice-amount {
      color: #d32f2f;
      font-weight: 700;
      white-space: nowrap;
    }

    .action-buttons {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 4px;
    }

    .action-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 100%;
      padding: 12px;
    }

    .select-button {
      margin-top: 4px;
    }

    @media (max-width: 600px) {
      .dialog-container {
        min-width: 94vw;
        max-width: 94vw;
      }
    }
  `]
})
export class CustomerDetailsDialogComponent implements OnInit {
  pendingInvoices: PendingInvoiceListItem[] = [];
  invoicesLoaded = false;
  loadingInvoices = false;
  invoiceError: string | null = null;
  balanceAmount = 0;
  balanceCurrency = 'INR';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { customer: Customer; showSelect?: boolean },
    private dialogRef: MatDialogRef<CustomerDetailsDialogComponent>,
    private dialog: MatDialog,
    private deliveryService: DeliveryService
  ) { }

  ngOnInit(): void {
    this.balanceAmount = Number(this.data.customer.outstandingBalance || 0);
    this.balanceCurrency = this.data.customer.currency || 'INR';
  }

  onClose(): void {
    this.dialogRef.close();
  }

  selectCustomer(): void {
    this.dialogRef.close({ select: true, loadMode: 'ask' });
  }

  loadLastInvoice(): void {
    this.dialogRef.close({ select: true, loadMode: 'last_invoice' });
  }

  onPhoneClick(event: Event): void {
    event.stopPropagation();
  }

  hasLocation(): boolean {
    return Number.isFinite(Number(this.data.customer.latitude)) && Number.isFinite(Number(this.data.customer.longitude))
      && !(Number(this.data.customer.latitude) === 0 && Number(this.data.customer.longitude) === 0);
  }

  openDirections(): void {
    if (!this.hasLocation()) return;
    const latitude = Number(this.data.customer.latitude);
    const longitude = Number(this.data.customer.longitude);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`, '_blank');
  }

  openMap(): void {
    const customer = this.data.customer;
    const latitude = customer.latitude;
    const longitude = customer.longitude;

    if (latitude && longitude) {
      const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  }

  openImagePopup(): void {
    if (this.data.customer.image) {
      this.dialog.open(ImagePopupComponent, {
        data: { image: this.data.customer.image, imageMimeType: this.data.customer.imageMimeType || 'image/jpeg' },
        panelClass: 'full-screen-popup',
        width: '100vw',
        height: '100vh',
        hasBackdrop: true,
        backdropClass: 'backdrop-clickable'
      });
    }
  }

  loadOutstandingInvoices(): void {
    const customerId = this.data.customer.id;
    if (!customerId || customerId <= 0 || this.data.customer.pendingSync) {
      this.invoicesLoaded = true;
      this.pendingInvoices = [];
      this.invoiceError = null;
      return;
    }

    this.loadingInvoices = true;
    this.invoiceError = null;
    this.pendingInvoices = [];

    this.deliveryService.getPendingInvoicesForCustomer(customerId, { limit: 100 }).subscribe({
      next: res => {
        this.loadingInvoices = false;
        this.invoicesLoaded = true;
        if (res.status === 'success') {
          this.pendingInvoices = res.invoices || [];
          if (res.summary) {
            this.balanceAmount = Number(res.summary.openTotal || this.balanceAmount || 0);
            this.balanceCurrency = res.summary.currency || this.balanceCurrency;
          }
        } else {
          this.pendingInvoices = [];
          this.invoiceError = res.message || 'Unable to load outstanding invoices.';
        }
      },
      error: err => {
        this.loadingInvoices = false;
        this.invoicesLoaded = true;
        this.pendingInvoices = [];
        this.invoiceError = err?.error?.message || err?.message || 'Unable to load outstanding invoices.';
      }
    });
  }
}
